/**
 * Tableau Provider Context
 * Ensures Tableau library is initialized once globally for the entire application
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { tableauService } from '../services/tableauService';
import '../utils/tableauErrorInterceptor'; // Initialize global error interceptor

interface TableauContextType {
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
  tableau: any;
}

const TableauContext = createContext<TableauContextType | undefined>(undefined);

interface TableauProviderProps {
  children: ReactNode;
}

export const TableauProvider: React.FC<TableauProviderProps> = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeTableau = async () => {
      try {
        console.log('TableauProvider: Starting global Tableau initialization');
        setIsLoading(true);
        setError(null);
        
        await tableauService.initialize({
          onReady: () => {
            console.log('TableauProvider: Tableau library is ready globally');
            setIsReady(true);
            setIsLoading(false);
          },
          onError: (err) => {
            console.error('TableauProvider: Tableau initialization error:', err);
            setError(err.message);
            setIsLoading(false);
          }
        });
      } catch (err: any) {
        console.error('TableauProvider: Tableau initialization failed:', err);
        setError(err.message || 'Failed to initialize Tableau');
        setIsLoading(false);
      }
    };

    initializeTableau();
  }, []);

  const value: TableauContextType = {
    isReady,
    isLoading,
    error,
    tableau: isReady ? tableauService.getTableau() : null
  };

  return (
    <TableauContext.Provider value={value}>
      {children}
    </TableauContext.Provider>
  );
};

export const useTableauContext = (): TableauContextType => {
  const context = useContext(TableauContext);
  if (context === undefined) {
    throw new Error('useTableauContext must be used within a TableauProvider');
  }
  return context;
};
